import axiosInstance from './axiosInstance'

// ── Interfaces ────────────────────────────────────────────────────────────────

export interface SalesOwner {
  id: string
  name: string
  avatarUrl?: string | null
}

export interface SalesDealInfo {
  id: string
  title: string
  stage: string
  amount: number | null
  currency: string
  status: string
  isWon: boolean
  isLost: boolean
  lostReason: string | null
  closedAt: string | null
  expectedCloseDate: string | null
  priority: string
  source: string | null
  description: string | null
  requirements?: string | null
  customerId: string | null
  contactId: string | null
  orgId: string | null
  lobId: string | null
  warehouseId: string | null
  orgName?: string | null
  lobName?: string | null
  warehouseName?: string | null
  customer: { id: string; name: string; companyName: string | null } | null
  contact: { id: string; name: string; phone: string } | null
  createdBy: SalesOwner | null
  updatedBy: SalesOwner | null
  owner: SalesOwner
  assignedToId: string | null
  assignedTo: SalesOwner | null
  createdAt: string
  updatedAt: string
  noteCount: number
  taskCount: number
  productCount: number
}

export interface SalesRecord {
  id: string
  orgId: string | null
  lobId: string | null
  warehouseId: string | null
  dealId: string | null
  ownerId: string
  ownerName: string
  assignedToId?: string | null
  assignedToName?: string | null
  assignedToEmail?: string | null
  dealName: string
  company: string | null
  contactName: string | null
  value: number
  closeDate: string | null
  sourceDealStage: string
  status: 'New' | 'Onboarded' | 'Won' | 'Lost'
  expiryDate?: string | null
  requirements?: string | null
  helpAssigneeId?: string | null
  helpAssignee?: string | null
  helpAssigneeEmail?: string | null
  createdAt: string
  updatedAt: string
  deal: SalesDealInfo | null
  onboard?: {
    id: string
    customerUserId: string | null
    orderId: string | null
    orderValue?: string | null
    createdAt: string
    updatedAt: string
  } | null
}

export interface SalesActivity {
  id: string
  salesId: string
  type: 'NOTE' | 'CALL' | 'EMAIL' | 'TASK' | 'EVENT' | 'WON' | 'STAGE_CHANGED' | 'REASSIGNED'
  title: string
  description: string | null
  performedBy: string
  performedByName: string
  createdAt: string
}

export interface SalesNote {
  id: string
  salesId: string
  content: string
  createdBy: { id: string; name: string; email: string; avatarUrl: string | null } | null
  createdAt: string
  updatedAt: string
}

export interface SalesTask {
  id: string
  salesId: string
  title: string
  description: string | null
  dueDate: string | null
  status: 'OPEN' | 'IN_PROGRESS' | 'DONE'
  priority: 'LOW' | 'MEDIUM' | 'HIGH'
  assignedToId: string | null
  assignedToName: string | null
  assignedToAvatar: string | null
  completedAt: string | null
  createdById: string | null
  createdByName: string | null
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

export interface SalesAttachmentItem {
  id: string
  salesId: string
  fileName: string
  url: string
  contentType: string
  sizeBytes: number
  uploadedById: string | null
  uploadedByName: string | null
  uploadedByAvatar: string | null
  createdAt: string
}

export interface CreateSalesActivityPayload {
  type: string
  title: string
  description?: string | null
}

export interface UpdateSalesActivityPayload {
  title?: string
  description?: string | null
}

export interface SalesTask {
  id: string
  salesId: string
  title: string
  description: string | null
  dueDate: string | null
  status: 'OPEN' | 'IN_PROGRESS' | 'DONE'
  priority: 'LOW' | 'MEDIUM' | 'HIGH'
  assignedToId: string | null
  assignedToName: string | null
  assignedToAvatar: string | null
  createdByName: string | null
  createdById: string | null
  completedAt: string | null
  isDeleted: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateSalesTaskPayload {
  title: string
  description?: string
  dueDate?: string
  priority?: string
  assignedToId?: string
}

export interface UpdateSalesTaskPayload {
  title?: string
  description?: string | null
  dueDate?: string | null
  status?: string
  priority?: string
  assignedToId?: string | null
}

export interface SalesCall {
  id: string
  salesId: string
  callType: string
  status: string
  outcome: string | null
  notes: string | null
  followUpDate: string | null
  startedAt: string
  endedAt: string | null
  durationSeconds: number | null
  recordingUrl?: string | null
  initiatedBy: { id: string; name: string; email: string; avatarUrl: string | null }
  createdAt: string
  updatedAt: string
}

export interface CreateSalesCallPayload {
  callType?: string
  status?: string
  outcome?: string
  notes?: string
  followUpDate?: string | null
  startedAt?: string
  durationSeconds?: number
}

export interface SalesFollowUp {
  id: string
  salesId: string
  title: string | null
  type: string
  scheduledAt: string
  endTime: string | null
  notes: string | null
  reminderMinutes: number | null
  status: string
  outcome: string | null
  assignedTo: { id: string; name: string; email: string; avatarUrl: string | null } | null
  createdBy: { id: string; name: string; email: string; avatarUrl: string | null } | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface CreateSalesFollowUpPayload {
  title?: string
  type: string
  scheduledAt: string
  endTime?: string
  notes?: string
  reminderMinutes?: number
  assignedToId?: string
}

export interface SalesFilters {
  search?: string
  stages?: string[]
  ownerIds?: string[]
  assignedToIds?: string[]
  orgIds?: string[]
  lobIds?: string[]
  warehouseIds?: string[]
  fromDate?: string
  toDate?: string
  // Price filters
  amountLt?:  number
  amountGt?:  number
  amountEq?:  number
  amountLte?: number
  amountGte?: number
  customerTypeIds?: number[]
  page?: number
  pageSize?: number
}

// ── Revenue Summary (from Onboard table) ─────────────────────────────────────
export interface SalesRevenue {
  totalRevenue:     number
  monthlyRevenue:   number
  quarterlyRevenue: number
  yearlyRevenue:    number
  totalSalesCount:  number
}

export type SalesRevenueFilters = Omit<SalesFilters, 'page' | 'pageSize'>

// ── API ───────────────────────────────────────────────────────────────────────

export const salesApi = {
  // ── List / Get ──────────────────────────────────────────────────────────────

  export: async (params?: SalesFilters): Promise<void> => {
    const res = await axiosInstance.get('/sales/export', { params, responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([res.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Sales.xlsx')
    document.body.appendChild(link)
    link.click()
    link.remove()
  },

  getRevenue: async (params?: SalesRevenueFilters): Promise<SalesRevenue> => {
    const res = await axiosInstance.get<{ data: SalesRevenue }>('/sales/revenue', { params })
    return res.data.data
  },

  getAll: async (params?: SalesFilters): Promise<{ total: number; page: number; pageSize: number; items: SalesRecord[] }> => {
    const res = await axiosInstance.get<{ data: { total: number; page: number; pageSize: number; items: SalesRecord[] } }>('/sales', { params })
    return res.data.data
  },

  getById: async (salesId: string): Promise<SalesRecord> => {
    const res = await axiosInstance.get<{ data: SalesRecord }>(`/sales/${salesId}`)
    return res.data.data
  },

  getByDealId: async (dealId: string): Promise<SalesRecord> => {
    const res = await axiosInstance.get<{ data: SalesRecord }>('/sales/deal/' + dealId)
    return res.data.data
  },

  // ── Activities (Timeline) ─────────────────────────────────────────────────

  getActivities: async (salesId: string): Promise<SalesActivity[]> => {
    const res = await axiosInstance.get<{ data: SalesActivity[] }>(`/sales/${salesId}/activities`)
    return res.data.data
  },

  addActivity: async (salesId: string, payload: CreateSalesActivityPayload): Promise<SalesActivity> => {
    const res = await axiosInstance.post<{ data: SalesActivity }>(`/sales/${salesId}/activities`, payload)
    return res.data.data
  },

  updateActivity: async (salesId: string, activityId: string, payload: UpdateSalesActivityPayload): Promise<SalesActivity> => {
    const res = await axiosInstance.put<{ data: SalesActivity }>(`/sales/${salesId}/activities/${activityId}`, payload)
    return res.data.data
  },

  deleteActivity: async (salesId: string, activityId: string): Promise<void> => {
    await axiosInstance.delete(`/sales/${salesId}/activities/${activityId}`)
  },

  // ── Notes ─────────────────────────────────────────────────────────────────

  getNotes: async (salesId: string): Promise<SalesNote[]> => {
    const res = await axiosInstance.get<{ data: SalesNote[] }>(`/sales/${salesId}/notes`)
    return res.data.data
  },

  addNote: async (salesId: string, content: string): Promise<SalesNote[]> => {
    const res = await axiosInstance.post<{ data: SalesNote[] }>(`/sales/${salesId}/notes`, { content })
    return res.data.data
  },

  updateNote: async (salesId: string, noteId: string, content: string): Promise<SalesNote[]> => {
    const res = await axiosInstance.put<{ data: SalesNote[] }>(`/sales/${salesId}/notes/${noteId}`, { content })
    return res.data.data
  },

  deleteNote: async (salesId: string, noteId: string): Promise<void> => {
    await axiosInstance.delete(`/sales/${salesId}/notes/${noteId}`)
  },

  // ── Tasks ─────────────────────────────────────────────────────────────────

  getTasks: async (salesId: string): Promise<SalesTask[]> => {
    const res = await axiosInstance.get<{ data: SalesTask[] }>(`/sales/${salesId}/tasks`)
    return res.data.data
  },

  addTask: async (salesId: string, payload: CreateSalesTaskPayload): Promise<SalesTask> => {
    const res = await axiosInstance.post<{ data: SalesTask }>(`/sales/${salesId}/tasks`, payload)
    return res.data.data
  },

  updateTask: async (salesId: string, taskId: string, payload: UpdateSalesTaskPayload): Promise<SalesTask> => {
    const res = await axiosInstance.put<{ data: SalesTask }>(`/sales/${salesId}/tasks/${taskId}`, payload)
    return res.data.data
  },

  deleteTask: async (salesId: string, taskId: string): Promise<void> => {
    await axiosInstance.delete(`/sales/${salesId}/tasks/${taskId}`)
  },

  // ── Calls ─────────────────────────────────────────────────────────────────

  getCalls: async (salesId: string): Promise<SalesCall[]> => {
    const res = await axiosInstance.get<{ data: SalesCall[] }>(`/sales/${salesId}/calls`)
    return res.data.data
  },

  addCall: async (salesId: string, payload: CreateSalesCallPayload): Promise<SalesCall> => {
    const res = await axiosInstance.post<{ data: SalesCall }>(`/sales/${salesId}/calls`, payload)
    return res.data.data
  },

  // ── Follow-ups (Meetings/Events) ───────────────────────────────────────────

  getFollowUps: async (salesId: string): Promise<SalesFollowUp[]> => {
    const res = await axiosInstance.get<{ data: SalesFollowUp[] }>(`/sales/${salesId}/follow-ups`)
    return res.data.data
  },

  addFollowUp: async (salesId: string, payload: CreateSalesFollowUpPayload): Promise<SalesFollowUp> => {
    const res = await axiosInstance.post<{ data: SalesFollowUp }>(`/sales/${salesId}/follow-ups`, payload)
    return res.data.data
  },

  completeFollowUp: async (salesId: string, followUpId: string, outcome?: string): Promise<SalesFollowUp> => {
    const res = await axiosInstance.patch<{ data: SalesFollowUp }>(`/sales/${salesId}/follow-ups/${followUpId}/complete`, { outcome })
    return res.data.data
  },

  cancelFollowUp: async (salesId: string, followUpId: string): Promise<SalesFollowUp> => {
    const res = await axiosInstance.patch<{ data: SalesFollowUp }>(`/sales/${salesId}/follow-ups/${followUpId}/cancel`)
    return res.data.data
  },

  updateSaleStatus: async (salesId: string, status: 'New' | 'Onboarded' | 'Won' | 'Lost'): Promise<{ id: string; status: 'New' | 'Onboarded' | 'Won' | 'Lost' }> => {
    const res = await axiosInstance.patch<{ data: { id: string; status: 'New' | 'Onboarded' | 'Won' | 'Lost' } }>(`/sales/${salesId}/status`, { status })
    return res.data.data
  },

  reassign: async (id: string, assigneeId: string): Promise<SalesRecord> => {
    const res = await axiosInstance.patch(`/sales/${id}/reassign`, { assigneeId })
    return res.data.data
  },

  updateRequirements: async (id: string, requirements: string | null): Promise<SalesRecord> => {
    const res = await axiosInstance.patch(`/sales/${id}/requirements`, { requirements })
    return res.data.data
  },

  updateOrderId: async (id: string, orderId: string | null, orderValue?: string | null): Promise<{ id: string; orderId: string | null; orderValue?: string | null }> => {
    const res = await axiosInstance.patch<{ data: { id: string; orderId: string | null; orderValue?: string | null } }>(`/sales/${id}/order-id`, { orderId, orderValue })
    return res.data.data
  },

  delete: async (salesId: string): Promise<void> => {
    await axiosInstance.delete(`/sales/${salesId}`)
  },

  simulateOrderPlacement: async (customerUserId: string, orderId: string): Promise<any> => {
    const res = await axiosInstance.post<{ data: any }>('/sales/onboard-order', { customerUserId, orderId })
    return res.data.data
  },

  updateHelpAssignee: async (id: string, helpAssigneeId: string | null): Promise<SalesRecord> => {
    const res = await axiosInstance.patch<{ data: SalesRecord }>(`/sales/${id}/help-assignee`, { helpAssigneeId })
    return res.data.data
  },

  getSummary: async (filters: SalesFilters = {}): Promise<SalesSummaryDto> => {
    const p: Record<string, any> = {}
    if (filters.orgIds?.length) p.orgIds = filters.orgIds
    if (filters.lobIds?.length) p.lobIds = filters.lobIds
    if (filters.fromDate) p.fromDate = filters.fromDate
    if (filters.toDate) p.toDate = filters.toDate

    const res = await axiosInstance.get<{ data: SalesSummaryDto }>('/sales/summary', {
      params: p,
      paramsSerializer: { indexes: null },
    })
    return res.data.data
  },
}

export interface SalesStatusSummaryDto {
  status: string
  count: number
}

export interface SalesSummaryDto {
  totalSales: number
  expectedValue: number
  statusSummaries: SalesStatusSummaryDto[]
}
